import React, { useMemo } from 'react';
import IndicadoresOdontogramaPanel from './IndicadoresOdontogramaPanel';
import PlanDiagnosticoMspTable from './PlanDiagnosticoMspTable';
import PlanTratamientoMspTable from './PlanTratamientoMspTable';
import ProfesionalAtencionCard from './ProfesionalAtencionCard';
import { diagnosticosConDatos } from '../../data/planDiagnosticoMsp';
import { tratamientosConDatos } from '../../data/planTratamientoMsp';
import { mspRegistroCompletoResumen } from '../../utils/mspReadHelpers';
import { HosIndexChips } from './FichaLecturaUi';

function MspFillGuide({ resumen, onEditFicha }) {
  const todoVacio = !resumen.indicadores && resumen.diagnosticos === 0 && resumen.tratamientos === 0;

  return (
    <div className={`msp-fill-guide${todoVacio ? ' msp-fill-guide--empty' : ''}`}>
      <div className="msp-fill-guide__icon" aria-hidden>
        <i className="bi bi-journal-medical" />
      </div>
      <div className="msp-fill-guide__text">
        <strong>¿De dónde salen estos datos?</strong>
        <p>
          No se generan solos al ver el odontograma. Se guardan en la{' '}
          <strong>ficha odontológica del paciente</strong>, sección{' '}
          <strong>«Indicadores MSP, índices, planes y profesional»</strong>, y al pulsar{' '}
          <strong>Guardar</strong> en el modal de la ficha.
        </p>
        <ul className="msp-fill-guide__list">
          <li>
            <span className="msp-fill-guide__dot msp-fill-guide__dot--hos" />
            H.O.S. (placa, cálculo, gingivitis por piezas índice)
          </li>
          <li>
            <span className="msp-fill-guide__dot msp-fill-guide__dot--cols" />
            Enfermedad periodontal, Angle, fluorosis
          </li>
          <li>
            <span className="msp-fill-guide__dot msp-fill-guide__dot--cpo" />
            Índices CPO-ceo (caries / perdidos / obturados)
          </li>
          <li>
            <span className="msp-fill-guide__dot msp-fill-guide__dot--diag" />
            Plan de diagnóstico y plan de tratamiento (tablas MSP)
          </li>
          <li>
            <span className="msp-fill-guide__dot msp-fill-guide__dot--prof" />
            Profesional de salud (nombre, código MSP)
          </li>
        </ul>
      </div>
      {onEditFicha ? (
        <button type="button" className="cp-btn cp-btn--primary cp-btn--sm msp-fill-guide__btn" onClick={onEditFicha}>
          <i className="bi bi-pencil-square" />
          Abrir ficha para completar
        </button>
      ) : null}
    </div>
  );
}

function MspReadBlock({ title, subtitle, icon, badge, accent, children }) {
  return (
    <section className="msp-read-block" style={{ '--msp-accent': accent }}>
      <header className="msp-read-block__hdr">
        <span className="msp-read-block__icon">
          <i className={`bi ${icon}`} />
        </span>
        <div className="msp-read-block__titles">
          <h4 className="msp-read-block__title">{title}</h4>
          {subtitle ? <p className="msp-read-block__sub">{subtitle}</p> : null}
        </div>
        {badge != null && badge !== '' ? <span className="msp-read-block__badge">{badge}</span> : null}
      </header>
      <div className="msp-read-block__body">{children}</div>
    </section>
  );
}

/**
 * Vista unificada de registros MSP en modo lectura (odontograma / historia clínica).
 */
export default function MspRegistroReadView({
  form,
  showIndicadores = true,
  showDiagnostico = true,
  showTratamiento = true,
  showProfesional = true,
  showEditHint = true,
  showFillGuide = true,
  onEditFicha = null,
  onAddTratamiento = null,
  compact = true,
}) {
  const resumen = useMemo(() => mspRegistroCompletoResumen(form || {}), [form]);
  const diagCount = resumen.diagnosticos;
  const tratCount = resumen.tratamientos;

  return (
    <div className={`msp-read-view${compact ? ' msp-read-view--compact' : ''}`}>
      {showFillGuide && <MspFillGuide resumen={resumen} onEditFicha={onEditFicha} />}

      {showIndicadores && (
        <MspReadBlock
          title="Indicadores de salud bucal"
          subtitle="H.O.S., periodontal, maloclusión, fluorosis e índices CPO-ceo"
          icon="bi-bar-chart-line-fill"
          accent="#13DEB9"
          badge={resumen.indicadores ? 'Con datos' : 'Pendiente'}
        >
          <IndicadoresOdontogramaPanel
            form={form}
            readOnly
            compact={compact}
            showHosTitles={false}
            showColsTitle={false}
            showCpoCeoTitle={false}
          />
          <HosIndexChips placa={form?.placa} calculo={form?.calculo} gingivitis={form?.gingivitis} />
        </MspReadBlock>
      )}

      {showDiagnostico && (
        <MspReadBlock
          title="Plan de diagnóstico"
          subtitle="Diagnóstico · CIE-10 · Presuntivo / Definitivo"
          icon="bi-clipboard2-pulse-fill"
          accent="#5D87FF"
          badge={diagCount > 0 ? diagCount : 'Pendiente'}
        >
          <PlanDiagnosticoMspTable rows={form?.tablaDiagnosticosMSP} readOnly showSectionTitle={false} />
        </MspReadBlock>
      )}

      {showTratamiento && (
        <MspReadBlock
          title="Plan de tratamiento"
          subtitle="Sesiones, procedimientos y prescripciones"
          icon="bi-bandaid-fill"
          accent="#FFAE1F"
          badge={tratCount > 0 ? tratCount : 'Pendiente'}
        >
          <PlanTratamientoMspTable rows={form?.tablaTratamientosMSP} readOnly showSectionTitle={false} />
          {onAddTratamiento ? (
            <div className="msp-read-trat-actions mt-3">
              <button type="button" className="cp-btn cp-btn--primary cp-btn--sm" onClick={onAddTratamiento}>
                <i className="bi bi-plus-lg" />
                Añadir sesión / procedimiento
              </button>
              <p className="small text-muted mb-0 mt-2">
                Abre la sección editable <strong>Indicadores MSP y planes</strong> para registrar nuevas sesiones sin cerrar la historia.
              </p>
            </div>
          ) : null}
        </MspReadBlock>
      )}

      {showProfesional && (
        <MspReadBlock
          title="Profesional que atiende"
          subtitle="Perfil del médico / odontólogo (consultorio)"
          icon="bi-person-badge-fill"
          accent="#7460EE"
        >
          <ProfesionalAtencionCard compact />
        </MspReadBlock>
      )}

      {showEditHint && !onEditFicha && (
        <p className="msp-read-edit-hint">
          <i className="bi bi-pencil-square" />
          Para editar: <strong>Editar ficha odontológica</strong> → Indicadores MSP → Guardar.
        </p>
      )}
    </div>
  );
}
